import { useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';

// MarketApts embeds the fee guide in an iframe that auto-resizes its height via
// their hosted iframe-resizer script. The per-property AIM code selects which
// community's fees to show (the `?code=` query param).
const IFRAME_RESIZER_SRC = 'https://www.marketapts.com/js/iframeResizer.min.js';

// Fallback used when a property has not been assigned an AIM code yet.
const DEFAULT_AIM_CODE = '1080WMRK';

declare global {
  interface Window {
    iFrameResize?: (options: Record<string, unknown>, target: string | HTMLElement) => void;
  }
}

/**
 * Property Fee Guide — inline MarketApts fee-guide iframe.
 *
 * Renders directly on the page (e.g. Floor Plans, below the apartment-sync
 * block) rather than as a modal launched by a CTA. The AIM property code
 * resolves from an editable settings slot group (with a suggested binding to
 * the property's `custom_aim_property_code` field), falling back to a shared
 * default so the embed always resolves to some community.
 */
export function PropertyFeeGuide() {
  // Editable per-page settings for the iframe. `aim_code` is a plain text
  // field so an editor can override the property's AIM code, but the
  // `defaultField` binding means it auto-fills from the property's
  // `custom_aim_property_code` custom field when unset.
  const settingsDef: SlotGroupDefinition = {
    label: 'Fee Guide Settings',
    fields: {
      aim_code: {
        label: 'AIM Property Code',
        type: 'text',
        defaultField: 'custom_aim_property_code',
      },
    },
  };

  const settings = useSlotGroup('propertyfeeguide.settings', settingsDef);

  // Belt-and-suspenders: the slot group's `defaultField` will populate
  // `aim_code` from the property record; keep a direct field read too so a
  // stale editor value can't outweigh a missing custom_aim_property_code.
  const fieldCode = usePropertyField('custom_aim_property_code') as string | null;
  const editorCode =
    typeof settings.aim_code === 'string' ? settings.aim_code.trim() : '';
  const aimCode = editorCode || fieldCode || DEFAULT_AIM_CODE;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Load / initialize the MarketApts iframe resizer once the iframe is
  // mounted. Rerunning on aimCode change is fine — src reset triggers a
  // fresh resize anyway.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const applyResize = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({ log: false }, iframeRef.current);
      }
    };

    if (window.iFrameResize) {
      applyResize();
      return;
    }

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${IFRAME_RESIZER_SRC}"]`,
    );
    if (!script) {
      script = document.createElement('script');
      script.src = IFRAME_RESIZER_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', applyResize);
    return () => {
      script?.removeEventListener('load', applyResize);
    };
  }, [aimCode]);

  const iframeSrc = `https://www.marketapts.com/iframes/fee-guide?code=${encodeURIComponent(
    aimCode,
  )}`;

  return (
    <section className="property-fee-guide bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 py-10 md:py-14">
      <div className="mx-auto w-full max-w-6xl">
        <RichTextContent
          path="propertyfeeguide.blurb"
          defaultValue=""
          as="div"
          className="mx-auto mb-6 max-w-3xl text-center font-body text-sm md:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_a]:underline"
        />
        <EditableSlotGroup
          groupPath="propertyfeeguide.settings"
          definition={settingsDef}
          className="row container p-0"
        >
          <iframe
            ref={iframeRef}
            id="fee-guide"
            title="Fee Guide"
            src={iframeSrc}
            style={{ border: 'none', width: '1px', minWidth: '100%' }}
          />
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFeeGuide',
  displayName: 'Property Fee Guide',
  tokens: {
    'propertyfeeguide.blurb': {
      label: 'Intro Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'propertyfeeguide.settings': {
      label: 'Fee Guide Settings',
      fields: {
        aim_code: {
          label: 'AIM Property Code',
          type: 'text',
          defaultField: 'custom_aim_property_code',
        },
      },
    },
  },
};

(PropertyFeeGuide as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PropertyFeeGuide;
